import React, { useEffect, useState } from "react";
import { Box, Text } from "components";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ImageContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const Label = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px;
  border-radius: 4px;
`;

const DownloadButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  &:active {
    background: rgba(255, 255, 255, 1);
    transform: scale(0.98);
  }
`;

const ContinueButton = styled.button`
  background: #4caf50;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 24px;

  &:active {
    background: #45a049;
    transform: scale(0.98);
  }
`;

function ImagePreviewModal({
  originalImage,
  processedImage,
  onClose,
  onContinue,
}) {
  const [originalUrl, setOriginalUrl] = useState(null);
  const [processedUrl, setProcessedUrl] = useState(null);

  useEffect(() => {
    if (originalImage) {
      const url = URL.createObjectURL(originalImage);
      setOriginalUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [originalImage]);

  useEffect(() => {
    if (processedImage) {
      const url = URL.createObjectURL(processedImage);
      setProcessedUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [processedImage]);

  const handleDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Overlay>
      <CloseButton onClick={onClose} aria-label="Close preview">
        ×
      </CloseButton>

      <Box mt="50px">
        <Text
          color="white"
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          mb="m"
        >
          📸 Captured Images
        </Text>

        <Box mt="xl">
          <ImageContainer>
            <Label>Original Photo (Color)</Label>
            {originalUrl && (
              <>
                <StyledImage src={originalUrl} alt="Original passport photo" />
                <DownloadButton
                  onClick={() =>
                    handleDownload(
                      originalImage,
                      `passport-original-${Date.now()}.jpg`
                    )
                  }
                >
                  ⬇️ Download Original
                </DownloadButton>
              </>
            )}
          </ImageContainer>

          <ImageContainer>
            <Label>Processed Photo (Sent to Backend)</Label>
            {processedUrl && (
              <>
                <StyledImage
                  src={processedUrl}
                  alt="Processed passport photo"
                />
                <DownloadButton
                  onClick={() =>
                    handleDownload(
                      processedImage,
                      `passport-processed-${Date.now()}.jpg`
                    )
                  }
                >
                  ⬇️ Download Processed
                </DownloadButton>
              </>
            )}
          </ImageContainer>

          <ContinueButton onClick={onContinue}>
            Continue & Validate Passport
          </ContinueButton>
        </Box>
      </Box>
    </Overlay>
  );
}

export default ImagePreviewModal;
